import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, CampoTexto, useAvisos, useFormulario, Ventana } from '@tallerya/ui';
import { useCambiosCatalogos, version, type Especialidad } from './datos';

// Alta y renombre de una especialidad (#154), con la misma ventana que la
// categoría: el Figma solo dibujó las categorías y las especialidades siguen ese
// diseño. Una especialidad también nace al escribirla en la ficha de un mecánico;
// esta es la forma de tener la lista armada antes.

export type EdicionEspecialidad = { tipo: 'nueva' } | { tipo: 'editar'; especialidad: Especialidad } | null;

interface Props {
  edicion: EdicionEspecialidad;
  onCerrar: () => void;
}

export function VentanaEspecialidad({ edicion, onCerrar }: Props) {
  const clave =
    edicion === null ? 'cerrada' : edicion.tipo === 'nueva' ? 'nueva' : String(edicion.especialidad.id);
  return <Contenido key={clave} edicion={edicion} onCerrar={onCerrar} />;
}

function Contenido({ edicion, onCerrar }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosCatalogos();
  const especialidad = edicion?.tipo === 'editar' ? edicion.especialidad : null;

  const form = useFormulario<{ name: string }>({
    inicial: { name: especialidad?.name ?? '' },
    guardar: (v) =>
      especialidad
        ? cambios.renombrarEspecialidad(String(especialidad.id), version(especialidad.version), v.name)
        : cambios.crearEspecialidad(v.name),
    camposPorCodigo: { 'specialty.nombre_duplicado': 'name', 'specialty.duplicada': 'name' },
  });

  async function guardar() {
    const nombre = form.valores.name.trim();
    if (!(await form.enviar())) return;
    avisar({
      titulo: especialidad
        ? t('admin.catalogos.especialidades.aviso.guardada', { nombre })
        : t('admin.catalogos.especialidades.aviso.creada', { nombre }),
    });
    onCerrar();
  }

  function cerrar() {
    if (form.guardando) return;
    onCerrar();
  }

  return (
    <Ventana
      abierta={edicion !== null}
      titulo={especialidad ? t('admin.catalogos.especialidades.editar') : t('admin.catalogos.especialidades.nueva')}
      info={t('admin.catalogos.especialidades.ventanaInfo')}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void guardar()}>
            {especialidad ? t('admin.catalogos.guardarCambios') : t('app.guardar')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="name"
        etiqueta={t('admin.catalogos.nombre')}
        obligatorio
        maximo={250}
        placeholder={t('admin.catalogos.especialidades.ejemplo')}
        autoComplete="off"
        ancho="completo"
      />
    </Ventana>
  );
}
